/**
 * The dialog chrome the menu's two panels share.
 *
 * Not a `<dialog>`: jsdom has no `showModal`, and the settings sheet already
 * carries its own hand-rolled equivalent for the same reason. This is the same
 * behaviour in one place — backdrop, Escape, focus in on open and back to the
 * trigger on close.
 */

import { useEffect, useRef, type ReactNode } from 'react'

interface OverlayProps {
  readonly title: string
  readonly onClose: () => void
  readonly children: ReactNode
}

export function Overlay({ title, onClose, children }: OverlayProps): React.JSX.Element {
  const panelRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    panelRef.current?.focus()

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [onClose])

  return (
    <>
      <div className="overlay__backdrop" onClick={onClose} aria-hidden="true" />
      <div
        className="overlay__panel"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        ref={panelRef}
      >
        <div className="overlay__head">
          <h2 className="overlay__title">{title}</h2>
          <button type="button" className="button overlay__close" onClick={onClose}>
            Schließen
          </button>
        </div>
        {children}
      </div>
    </>
  )
}
